import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { GoogleAuthProvider, OAuthProvider, signInWithEmailAndPassword, getAuth, signInWithPopup } from "firebase/auth";
import { CreateAccountApi, VerifyAccountApi } from "apis/authServices";
import { useToastStore } from "store/toastStore";
import axios from "axios";
import { getUserDetails } from "apis/homeServices";
import { useUserStore } from "store/userStore";
import OfferModal, { Offer } from "components/OfferModal";

const Login: React.FC = () => {
  const { setIsAuthenticated } = useStore();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [passworderror, setPassworderror] = useState<boolean>(false);
  const [passwordLengthError, setPasswordLengthError] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    // Try to load from localStorage on mount
    return localStorage.getItem("rememberMe") === "true";
  });
  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();
  const appleProvider = new OAuthProvider("apple.com");
  appleProvider.addScope("email");
  appleProvider.addScope("name");
  appleProvider.setCustomParameters({ locale: "fr" });

  const { hideToast } = useToastStore();
  const [banners, setBanners] = useState<Offer[]>([]);
  const [isBannerLoading, setIsBannerLoading] = useState<boolean>(false);
  const [bannerError, setBannerError] = useState<string>("");
  const [currentBannerIndex, setCurrentBannerIndex] = useState<number>(0);
  const [showOfferModal, setShowOfferModal] = useState<boolean>(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  // On mount, if rememberMe, load username and password
  useEffect(() => {
    const shouldRemember = localStorage.getItem("rememberMe") === "true";
    if (shouldRemember) {
      const savedUsername = localStorage.getItem("rememberedUsername") || "";
      const savedPassword = localStorage.getItem("rememberedPassword") || "";
      setUsername(savedUsername);
      setPassword(savedPassword);
    }
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      setIsBannerLoading(true);
      setBannerError("");
      try {
        const { data } = await axios.get("https://staapi.vofoxsolutions.com/api/Public/GetActiveSignupBanners");
        if (Array.isArray(data)) {
          setBanners(data);
          setCurrentBannerIndex(0);
        } else {
          setBannerError("Unable to load offers right now.");
        }
      } catch (error) {
        console.error("Failed to fetch signup banners:", error);
        setBannerError("Unable to load offers right now.");
      } finally {
        setIsBannerLoading(false);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    if (currentBannerIndex >= banners.length) {
      setCurrentBannerIndex(0);
    }
  }, [banners.length, currentBannerIndex]);

  const showPreviousBanner = () => {
    setCurrentBannerIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  };

  const showNextBanner = () => {
    setCurrentBannerIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    showLoader();

    try {
      if (!username) {
        setError(`Email is required`);
        if (usernameRef.current) {
          usernameRef.current.focus();
        }
        return;
      }
      if (!username.includes("@")) {
        setError("Please enter a valid email address.");
        if (usernameRef.current) {
          usernameRef.current.focus();
        }
        return;
      }
      if (!password) {
        setError(`Password is required`);
        if (passwordRef.current) {
          passwordRef.current.focus(); // <-- Focus password input
        }
        return;
      }

      const verifyResponse = await VerifyAccountApi(username);
      console.log("VerifyAccount API Response:", verifyResponse);

      const statusId = verifyResponse?.statusId ?? null;
      console.log("Status ID:", statusId);

      switch (statusId) {
        case 1: {
          const auth = getAuth(); // ✅ Lexical declaration in block scope
          // Inactive → Redirect to Create Account
          console.log("🔄 Email exists but inactive. Redirecting to Create Account...");
          useToastStore.getState().showToast("Inactive account. Reactivate your account...", "info");
          await signInWithEmailAndPassword(auth, username, password);
          setTimeout(() => navigate("/createAccount"), 3000);

          return;
        }

        case 2: // Active → Proceed to login
          console.log("Email active. Proceeding to login...");
          break;

        case 3: // Blocked → Show error
        case 4: // Deleted → Show error
          setError(verifyResponse.message);
          return;

        case null: // Email does not exist
          useToastStore.getState().showToast("This email does not belong to an account. Please use an existing email or sign up.", "error");
          return;

        default:
          setError("Unexpected response from the server. Please try again.");
          return;
      }

      //Proceed with login if status is Active (case 2)
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, username, password);
      const user = userCredential.user;
      const idToken = await user.getIdToken();

      console.log("User logged in:", user);
      console.log("ID Token:", idToken);
      setIsAuthenticated(true);
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("rememberedUsername", username);
        localStorage.setItem("rememberedPassword", password);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("rememberedUsername");
        localStorage.removeItem("rememberedPassword");
      }

      // Fetch user details to get role and navigate accordingly
      try {
        const userDetails: any = await getUserDetails();
        console.log("User details fetched:", userDetails);

        // Store user details in userStore
        useUserStore.getState().setUsername(userDetails.userName);
        useUserStore.getState().setEmail(userDetails.email);
        useUserStore.getState().setUserRole(userDetails.role);
        useUserStore.getState().setReferralCode(userDetails.referralCode);

        // Navigate based on role
        if (userDetails.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/search");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        // Fallback to /search if user details fetch fails
        navigate("/search");
      }
    } catch (error: any) {
      setError("*Login failed. Please check your credentials.");
      setPassworderror(true);
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const signInWithGoogle = async () => {
    setIsLoading(true);
    showLoader();
    setError(""); // Clear any previous errors
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      console.log("Google sign-in successful:", user);

      // Verify account status
      const email = user.email;
      if (!email) {
        setError("Error: Unable to retrieve email from Google.");
        useToastStore.getState().showToast("Error: Unable to retrieve email from Google.", "error");
        return;
      }

      const verifyResponse = await VerifyAccountApi(email);
      const statusId = verifyResponse?.statusId ?? null;

      switch (statusId) {
        case 1: // Inactive
          useToastStore.getState().showToast("Inactive account. Reactivate your account...", "info");
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
        case 3: // Blocked
        case 4: // Deleted
          setError(verifyResponse.message || "Account is blocked or deleted.");
          useToastStore.getState().showToast(verifyResponse.message || "Account is blocked or deleted.", "error");
          return;
        case null: // Email does not exist
          useToastStore.getState().showToast("Inactive account. Reactivate your account...", "info");
        await CreateAccountApi();
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
      }

      setIsAuthenticated(true);
      useToastStore.getState().showToast("Successfully signed in with Google!", "success");

      // Fetch user details to get role and navigate accordingly
      try {
        const userDetails: any = await getUserDetails();
        console.log("User details fetched:", userDetails);

        // Store user details in userStore
        useUserStore.getState().setUsername(userDetails.userName);
        useUserStore.getState().setEmail(userDetails.email);
        useUserStore.getState().setUserRole(userDetails.role);
        useUserStore.getState().setReferralCode(userDetails.referralCode);

        // Navigate based on role
        if (userDetails.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/search");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        useToastStore.getState().showToast("Error fetching user details. Redirecting...", "info");
        // Fallback to /search if user details fetch fails
        navigate("/search");
      }
    } catch (error: any) {
      console.error("Google sign-in error:", error);

      // Handle specific Firebase Auth errors
      let errorMessage = "Google sign-in failed.";

      if (error.code) {
        switch (error.code) {
          case "auth/popup-closed-by-user":
            errorMessage = "Sign-in was cancelled. Please try again.";
            break;
          case "auth/popup-blocked":
            errorMessage = "Popup was blocked by your browser. Please allow popups and try again.";
            break;
          case "auth/cancelled-popup-request":
            errorMessage = "Only one popup request is allowed at a time. Please try again.";
            break;
          case "auth/network-request-failed":
            errorMessage = "Network error. Please check your connection and try again.";
            break;
          case "auth/account-exists-with-different-credential":
            errorMessage = "An account already exists with the same email address but different sign-in credentials.";
            break;
          default:
            errorMessage = error.message || "Google sign-in failed. Please try again.";
        }
      } else {
        errorMessage = error.message || "Google sign-in failed. Please try again.";
      }

      setError(errorMessage);
      useToastStore.getState().showToast(errorMessage, "error");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const signInWithApple = async () => {
    setIsLoading(true);
    showLoader();
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;
      const credential = OAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;
      const idToken = credential?.idToken;

      console.log("Apple sign-in successful:", { user, accessToken, idToken });

      // Verify account status
      const email = user.email;
      if (!email) {
        setError("Error: Unable to retrieve email from Apple.");
        return;
      }

      const verifyResponse = await VerifyAccountApi(email);
      const statusId = verifyResponse?.statusId ?? null;

      switch (statusId) {
        case 1: // Inactive
          useToastStore.getState().showToast("Inactive account. Reactivate your account...", "info");
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
        case 3: // Blocked
        case 4: // Deleted
          setError(verifyResponse.message);
          return;
        case null: // Email does not exist
          // useToastStore.getState().showToast("This email does not belong to an account. Please use an existing email or sign up.", "error");
          // return;
          useToastStore.getState().showToast("Inactive account. Reactivate your account...", "info");
          await CreateAccountApi();
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
      }

      setIsAuthenticated(true);

      // Fetch user details to get role and navigate accordingly
      try {
        const userDetails: any = await getUserDetails();
        console.log("User details fetched:", userDetails);

        // Store user details in userStore
        useUserStore.getState().setUsername(userDetails.userName);
        useUserStore.getState().setEmail(userDetails.email);
        useUserStore.getState().setUserRole(userDetails.role);
        useUserStore.getState().setReferralCode(userDetails.referralCode);

        // Navigate based on role
        if (userDetails.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/search");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        // Fallback to /search if user details fetch fails
        navigate("/search");
      }
    } catch (error: any) {
      console.error("Apple sign-in error:", error);
      setError(error.message || "Apple sign-in failed.");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  // Memoize password toggle SVG to prevent re-renders
  const passwordToggleIcon = useMemo(
    () => (
      <svg
        className="shrink-0 size-3.5"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path className={showPassword ? "hidden" : ""} d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
        <path className={showPassword ? "hidden" : ""} d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
        <path className={showPassword ? "hidden" : ""} d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
        <line className={showPassword ? "hidden" : ""} x1="2" x2="22" y1="2" y2="22" />
        <path className={showPassword ? "" : "hidden"} d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
        <circle className={showPassword ? "" : "hidden"} cx="12" cy="12" r="3" />
      </svg>
    ),
    [showPassword]
  );

  return (
    <div className="lg:col-span-3 col-span-12 lg:col-start-9">
      <div>
        <img className="w-40" src="/images/logo.svg" alt="logo" />
        <p className="text-sm mt-8 font-normal text-grease-50">Welcome Back, You’ve been missed.</p>
        <p className="text-2xl mt-3 font-semibold text-grease-100">Sign in to your account</p>
        <div className="flex gap-3 mt-8">
          <div
            onClick={signInWithGoogle}
            className="p-3 border border-lightgray-50 rounded-lg flex gap-3 items-center flex-1 cursor-pointer"
          >
            <img className="w-5" src="/images/_Google.svg" alt="Google logo" />
            <p className="text-grease-50 text-sm font-medium">Google</p>
          </div>
          <div
            onClick={signInWithApple}
            className="p-3 border border-lightgray-50 rounded-lg flex gap-3 items-center flex-1 cursor-pointer"
          >
            <img className="w-5" src="/images/apple.svg" alt="Apple logo" />
            <p className="text-grease-50 text-sm font-medium">Apple</p>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4">
          <div className="w-full h-0.25 bg-lightgray-100"></div>
          <p className="text-grease-100 text-xs">Or</p>
          <div className="w-full h-0.25 bg-lightgray-100"></div>
        </div>
        <form onSubmit={handleSubmit} className="mt-4">
          <input
            type="text"
            name="username"
            id="username"
            ref={usernameRef}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="block w-full p-4 rounded-xl bg-gray-50 text-sm text-grease-100 placeholder-gray-400 focus:outline-none border-0 focus:border-light-yellow border-transparent focus:border focus:ring-1 focus:ring-light-yellow"
            placeholder="Email Address"
          />
          <div className="relative mt-4">
            <input
              id="hs-toggle-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > 20) {
                  setPasswordLengthError("Password must be 20 characters or less");
                  setPassword(value.substring(0, 20));
                } else {
                  setPasswordLengthError("");
                  setPassword(value);
                }
              }}
              onKeyDown={(e) => {
                if (password.length >= 20 && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Delete' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' &&
                    e.key !== 'Tab' &&
                    e.key !== 'Home' &&
                    e.key !== 'End' &&
                    !e.ctrlKey && 
                    !e.metaKey &&
                    !e.altKey) {
                  e.preventDefault();
                  setPasswordLengthError("Password must be 20 characters or less");
                }
              }}
              ref={passwordRef}
              minLength={6}
              maxLength={20}
              className="block w-full p-4 rounded-xl bg-gray-50 text-sm text-grease-100 placeholder-gray-400 focus:outline-none border-0 focus:border-light-yellow border-transparent focus:border focus:ring-1 focus:ring-light-yellow"
              placeholder="Enter password"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none focus:text-light-yellow"
            >
              {passwordToggleIcon}
            </button>
          </div>
          {passwordLengthError && <p className="text-red-500 mt-2 text-sm">{passwordLengthError}</p>}
          {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
          <div className="flex items-center justify-between mt-6">
            <div className="flex items-center">
              <input
                id="checked-checkbox"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-light-yellow bg-gray-100 border-light-yellow rounded focus:outline-none"
              />
              <label htmlFor="checked-checkbox" className="ms-2 text-xs font-medium text-grease-100">
                Remember me
              </label>
            </div>
            <button
              type="button"
              className={`text-xs cursor-pointer bg-transparent border-none p-0 ${passworderror ? "text-red-500 font-semibold" : "text-light-yellow"}`}
              onClick={() => {
                navigate("/forgetPassword");
              }}
            >
              Forgot password?
            </button>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full p-4 rounded-lg text-white text-center shadow-button bg-light-yellow text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Logging In..." : "Log In"}
          </button>
        </form>
        <div className="flex items-center justify-center text-sm gap-2 mt-6">
          <p className="text-grease-200">Don't have an account?</p>
          <button
            type="button"
            onClick={() => {
              hideToast();
              navigate("/signup");
            }}
            className="text-light-yellow cursor-pointer bg-transparent border-none p-0"
          >
            Sign Up
          </button>
        </div>
        {(isBannerLoading || bannerError || banners.length > 0) && (
          <div className="mt-8">
            {isBannerLoading ? (
              <p className="text-xs text-grease-200 text-center">Loading offers...</p>
            ) : bannerError ? (
              <p className="text-xs text-red-500 text-center">{bannerError}</p>
            ) : banners.length > 0 ? (
              <>
                {/* New Banner Design */}
                <div className="relative bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
                  <div className="bg-red-50 p-2 rounded-lg shrink-0">
                    <img src="/images/gift.png" width="24" height="24" alt="Gift" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-grease-100 font-medium">
                      New offers are waiting for you!{" "}
                      <button
                        onClick={() => setShowOfferModal(true)}
                        className="text-light-yellow font-semibold hover:underline focus:outline-none"
                      >
                        click here
                      </button>{" "}
                      to explore.
                    </p>
                  </div>
                  <button
                    onClick={() => setBanners([])} // Or some other way to dismiss
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <i className="st-close text-xs"></i>
                  </button>
                </div>

                {/* Offer Modal */}
                <OfferModal
                  isOpen={showOfferModal}
                  onClose={() => setShowOfferModal(false)}
                  offer={banners[currentBannerIndex]}
                  currentIndex={currentBannerIndex}
                  totalOffers={banners.length}
                  onNext={showNextBanner}
                  onPrev={showPreviousBanner}
                />
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
